// One panel inside a report — runs the panel's SQL, dispatches to the
// appropriate viz component, and provides a manual refresh button.
//
// Auto-update strategy when `forceAuto` is on:
//   - parseWatchable(sql) decides if the SQL is a single-table SELECT.
//     If so, open WATCH on that table and re-fetch on each notify,
//     debounced by DEBOUNCE_MS (so a flurry of writes only triggers one
//     refresh).
//   - Otherwise (SHOW commands, multi-table SELECTs, etc.) fall back to
//     a setInterval poll every POLL_INTERVAL_MS.
//
// Manual refresh works in both modes — the refresh button bumps the
// internal refresh key, which the data hook reacts to.

import { useEffect, useRef, useState } from 'react';
import { KineDB } from '@kinedb/client';
import { getSocket } from '../../data/socket.js';
import { parseWatchable } from '../../data/sql-watch.js';
import { resolveTemplateSql } from '../../data/report-time.js';
import { I } from '../icons.jsx';
import { KpiViz } from './viz/KpiViz.jsx';
import { TableViz } from './viz/TableViz.jsx';
import { BarViz } from './viz/BarViz.jsx';
import { PieViz } from './viz/PieViz.jsx';
import { LineViz } from './viz/LineViz.jsx';

const DEBOUNCE_MS = 500;
const POLL_INTERVAL_MS = 2000;

// Run a single panel's SQL on demand. Refetches whenever `refreshKey`
// changes — call sites bump it from a refresh button or, when auto is
// on, from WATCH-driven debounce / interval polling. `{{since:..}}` /
// `{{until}}` placeholders (see report-time.js) resolve at fetch time so
// relative windows slide forward with wall-clock; `timeRange` is the
// report-level override (preset or zoom) and also triggers a refetch.
function usePanelData(sql, baseUrl, refreshKey, timeRange) {
  const [state, setState] = useState({ columns: null, rows: null, error: null, loading: true });
  useEffect(() => {
    if (!sql || !sql.trim()) {
      setState({ columns: null, rows: null, error: 'No query', loading: false });
      return;
    }
    let cancelled = false;
    setState(s => ({ ...s, loading: true }));
    const db = new KineDB(baseUrl);
    db.sql(resolveTemplateSql(sql, timeRange)).then(r => {
      if (cancelled) return;
      setState({
        columns: r.columns ?? [],
        rows: r.data ?? [],
        error: null,
        loading: false,
      });
    }).catch(e => {
      if (cancelled) return;
      setState(s => ({ ...s, error: e.message || String(e), loading: false }));
    });
    return () => { cancelled = true; };
  }, [sql, baseUrl, refreshKey, timeRange]);
  return state;
}

function dispatchViz(panel, data, zoom) {
  switch (panel.viz) {
    case 'kpi':   return <KpiViz   {...data} panel={panel} />;
    case 'table': return <TableViz {...data} panel={panel} />;
    case 'bar':   return <BarViz   {...data} panel={panel} />;
    case 'pie':   return <PieViz   {...data} panel={panel} />;
    case 'line':  return <LineViz  {...data} panel={panel} {...zoom} />;
    default:
      return <div className="report-viz-empty error">Unknown viz: {String(panel.viz)}</div>;
  }
}

export function Panel({
  panel, baseUrl = '', autoEffective = false, onToggleAuto,
  timeRange = null, onZoom, onZoomOut,
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  // Bumped when the tab returns to the foreground. Mobile browsers (esp. iOS
  // Safari) suspend a backgrounded tab — timers pause and the WebSocket is
  // dropped — and nothing re-establishes the WATCH subscription on return. We
  // include `wakeKey` in the auto-update effect deps so coming back re-subscribes
  // (the socket auto-reconnects), and force one immediate refetch so the panel
  // shows current data the moment you look at it again.
  const [wakeKey, setWakeKey] = useState(0);
  const data = usePanelData(panel.sql, baseUrl, refreshKey, timeRange);

  // Stable reference for the WATCH callback so the closure inside
  // useEffect can clear/reset its debounce timer without retriggering
  // the effect.
  const debouncedRefresh = useRef(null);

  // Track current auto state without re-binding the visibility listener.
  const autoRef = useRef(autoEffective);
  autoRef.current = autoEffective;

  // Resume live updates after the tab was actually BACKGROUNDED and returns.
  // Critically, we react ONLY to a real hidden→visible transition (tracked by
  // `wasHidden`) — NOT to plain window `focus`, which fires on ordinary clicks
  // and would otherwise tear down + rebuild the WATCH subscription constantly,
  // breaking auto-update. On a genuine return we refetch once and bump `wakeKey`
  // to re-establish the subscription / restart the poll.
  const wasHiddenRef = useRef(false);
  useEffect(() => {
    const wake = () => {
      if (!autoRef.current) return;
      setRefreshKey(k => k + 1);
      setWakeKey(k => k + 1);
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        wasHiddenRef.current = true;
      } else if (wasHiddenRef.current) {
        wasHiddenRef.current = false;
        wake();
      }
    };
    // `pageshow` with persisted=true = restored from the bfcache (a real
    // navigation-away then back), which can skip visibilitychange.
    const onPageShow = (e) => { if (e.persisted) wake(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  useEffect(() => {
    if (!autoEffective) return;

    // `panel.autoPoll` forces fixed-interval polling instead of WATCH. WATCH is
    // efficient for rarely-changing tables, but it depends on a live socket +
    // server push and is fragile across reconnects; for a fast-changing metrics
    // dashboard, polling is simpler and can't get into a broken-subscription
    // state. Polling is also used as the fallback for non-watchable SQL.
    const watchable = parseWatchable(panel.sql);
    const usePoll = panel.autoPoll || !watchable.ok;
    let cancelled = false;
    let pollTimer = null;
    let watchSub = null;
    let debounceTimer = null;

    const fire = () => {
      if (cancelled) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (!cancelled) setRefreshKey(k => k + 1);
      }, DEBOUNCE_MS);
    };
    debouncedRefresh.current = fire;

    if (!usePoll) {
      // Open WATCH on the singleton socket. Errors fall back silently —
      // the manual refresh button still works, and if the socket comes
      // back later the next render re-runs this effect.
      getSocket().then(s => {
        if (cancelled) return;
        s.watch(watchable.table, fire).then(sub => {
          if (cancelled) { sub.cancel?.(); return; }
          watchSub = sub;
        }).catch(() => { /* watch failed; ignore */ });
      }).catch(() => { /* socket unavailable; ignore */ });
    } else {
      // Non-watchable SQL (SHOW commands, multi-table SELECT, etc.) —
      // poll on a fixed interval. No debounce here since the interval
      // already paces it.
      pollTimer = setInterval(() => {
        if (!cancelled) setRefreshKey(k => k + 1);
      }, POLL_INTERVAL_MS);
    }

    return () => {
      cancelled = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (pollTimer) clearInterval(pollTimer);
      if (watchSub) {
        try { watchSub.cancel?.(); } catch { /* socket may be closed */ }
      }
    };
  }, [autoEffective, panel.sql, wakeKey]);

  const watchable = parseWatchable(panel.sql);
  const autoMode = autoEffective
    ? ((watchable.ok && !panel.autoPoll) ? `WATCH ${watchable.table}` : `polling ${POLL_INTERVAL_MS}ms`)
    : null;

  return (
    <section className={`report-panel report-panel-${panel.width || 'full'}`}>
      <header className="report-panel-head">
        <div className="report-panel-title">{panel.title || '(untitled panel)'}</div>
        <div className="report-panel-actions">
          {data.loading && <span className="report-panel-loading">…</span>}
          {onToggleAuto && (
            <button
              type="button"
              className={'btn btn-sm ' + (autoEffective ? 'btn-primary' : 'btn-ghost')}
              title={autoEffective ? `Auto on (${autoMode}) — click to pause` : 'Auto off — click to live-update'}
              onClick={onToggleAuto}
            >
              <I.Refresh size={11} /> Auto
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            title="Refresh this panel"
            onClick={() => setRefreshKey(k => k + 1)}
          >
            <I.Refresh size={12} />
          </button>
        </div>
      </header>
      <div className="report-panel-body">
        {dispatchViz(panel, data, { timeRange, onZoom, onZoomOut })}
      </div>
    </section>
  );
}
